import FeatureGates, { PerimeterType } from '@atlaskit/feature-gate-js-client';
import FeatureGateSingleFetchProvider from '@atlaskit/feature-gate-single-fetch-provider';
import { ConfigClient } from '@atlaskit/web-config-client';

import { isFedRamp } from '@atlassian/atl-context';

import type { SessionDataType } from '@confluence/session-data/entry-points/SessionDataTypes';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';

import { getStatsigEnvironment } from './getStatsigEnvironment';
import StatsigConfigurations from './StatsigConfigurations';
import { getFeatureFlagsServiceApiKey } from './getFeatureFlagsServiceApiKey';

export type StatsigOptions = {
	userId: SessionDataType['userId'];
	cloudId: SessionDataType['cloudId'] | null;
	environment: SessionDataType['environment'] | string;
};

export async function initializeClientWithProvider({
	cloudId,
	environment,
	userId,
}: StatsigOptions) {
	// https://bitbucket.org/atlassian/feature-flag-service/src/a6adccc26a95818c8128c63e4eb817414793b3c2/src/main/resources/application.yml#lines-386
	const TARGET_APP = 'confluence_web';

	try {
		const provider = new FeatureGateSingleFetchProvider({
			apiKey: getFeatureFlagsServiceApiKey(environment),
		});

		const [configClientInstance] = await Promise.all([
			ConfigClient.fetch({
				// Base URL of the feature-flag-service
				ffsBaseUrl: '/gateway/api/flags',
				ffsApiKey: getFeatureFlagsServiceApiKey(environment),
				// User context to evaluate the configuration against
				context: {
					namespace: TARGET_APP,
					identifiers: {
						atlassianAccountId: userId || undefined,
						tenantId: cloudId || undefined,
					},
					metadata: {},
				},
			}),
			FeatureGates.initializeWithProvider(
				{
					environment: getStatsigEnvironment(environment),
					targetApp: TARGET_APP,
					perimeter: isFedRamp() ? PerimeterType.FEDRAMP_MODERATE : PerimeterType.COMMERCIAL,
				},
				provider,
				{
					atlassianAccountId: userId || undefined,
					tenantId: cloudId || undefined,
				},
			),
		]);

		// StatsigConfigurations reads FeatureGates this must be initialized after FeatureGates
		StatsigConfigurations.initialize(configClientInstance);
	} catch (error: unknown) {
		if (error instanceof Error) {
			getMonitoringClient().submitError(error, {
				attribution: 'statsig',
			});
		}
	}
}
